import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Play, Users, Download as DownloadIcon, ListMusic, Music2, Globe2, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAnalytics } from "@/lib/afmApi";
import { fmtNum, fmtPct } from "@/lib/format";
import {
  platformColor, platformAbbr, platformGradient, platformGlow, platformId,
  flagEmoji, citySlug, countryCode, buildBreakdown,
} from "@/lib/metrics";
import { BreakdownModal } from "@/components/BreakdownModal";
import { StatCard } from "@/components/StatCard";
import { FlowLines } from "@/components/FlowLines";
import { SimuladoBadge } from "@/components/SimuladoBadge";
import { Loading, EmptyState, ErrorState, SectionTitle } from "@/components/common";

const RANGES = [
  { key: "7d", label: "7 días" },
  { key: "28d", label: "28 días" },
  { key: "1y", label: "1 año" },
  { key: "all", label: "Todo" },
];

const chartTickDate = (d) => {
  const [, m, day] = d.split("-");
  return `${day}/${m}`;
};

const AGE_COLORS = ["#9E1B22", "#CA0808", "#B82832", "#D45059", "#7A1419", "#E8898D"];

// Platform bar — brand color with soft gradient, rounded, subtle glow, clickable.
const PlatformBar = ({ p, max, onClick }) => {
  const color = platformColor(p.name);
  return (
    <button onClick={onClick} data-testid={`platform-bar-${p.name}`} className="group block w-full text-left">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg text-[10px] font-bold text-white shadow-sm" style={{ backgroundColor: color }}>{platformAbbr(p.name)}</span>
          <span className="font-medium text-[#171717] transition-colors group-hover:text-[#9E1B22]">{p.name}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="font-display text-lg leading-none text-[#171717]">{fmtNum(p.streams)}</span>
          <span className="rounded-full bg-[#9E1B22]/10 px-2 py-0.5 text-[10px] font-bold text-[#9E1B22]">{fmtPct(p.pct)}</span>
        </span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-black/5">
        <div
          className="h-full rounded-full transition-all duration-700 group-hover:brightness-110"
          style={{ width: `${max ? (p.streams / max) * 100 : 0}%`, background: platformGradient(p.name), boxShadow: platformGlow(p.name) }}
        />
      </div>
    </button>
  );
};

// Vinotinto geo card (countries / cities) — white high-contrast bars, clickable rows.
const GeoCard = ({ title, icon: Icon, rows, withFlags, onRow, testId }) => {
  const max = Math.max(...rows.map((r) => r.streams || 0), 1);
  return (
    <div className="rounded-2xl p-6 text-white shadow-[0_10px_30px_rgba(158,27,34,0.18)]" style={{ backgroundColor: "#9E1B22" }} data-testid={testId}>
      <div className="mb-5 flex items-center gap-2">
        <Icon className="h-4 w-4 text-white/80" />
        <h3 className="font-serif text-base font-bold">{title}</h3>
      </div>
      <div className="space-y-3.5">
        {rows.map((r, i) => (
          <button key={r.name} onClick={() => onRow(r)} data-testid={`${testId}-row-${i}`} className="group block w-full text-left">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <span className="w-4 shrink-0 font-mono text-xs text-white/50">{i + 1}</span>
                {withFlags && <span className="text-base leading-none">{flagEmoji(r.name)}</span>}
                <span className="truncate font-medium">{r.name}</span>
              </span>
              <span className="shrink-0 font-mono text-xs tabular-nums text-white/90">{fmtPct(r.pct)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.22)" }}>
              <div className="h-full rounded-full transition-all duration-700 group-hover:opacity-80" style={{ width: `${(r.streams / max) * 100}%`, backgroundColor: "#FFFFFF" }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [range, setRange] = useState("28d");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [bd, setBd] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (status === "ok" && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [location, status]);

  const load = () => {
    setStatus("loading");
    getAnalytics(user.id, range).then((d) => { setData(d); setStatus("ok"); }).catch(() => setStatus("error"));
  };
  useEffect(load, [user.id, range]);

  const openPlatform = (p) => setBd(buildBreakdown({ kind: "platform", id: platformId(p.name), name: p.name, streams: p.streams, pct: p.pct, series: data.series, topTracks: data.top_tracks }));
  const openCountry = (r) => setBd(buildBreakdown({ kind: "country", id: countryCode(r.name) || r.name, name: r.name, streams: r.streams, pct: r.pct, series: data.series, topTracks: data.top_tracks }));
  const openCity = (r) => setBd(buildBreakdown({ kind: "city", id: citySlug(r.name), name: r.name, streams: r.streams, pct: r.pct, series: data.series, topTracks: data.top_tracks }));

  const RangeSelector = (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="range-selector">
      {RANGES.map((r) => (
        <button key={r.key} data-testid={`range-selector-${r.key}`} onClick={() => setRange(r.key)}
          className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${range === r.key ? "bg-black text-white" : "text-slate-500 hover:text-black"}`}>
          {r.label}
        </button>
      ))}
    </div>
  );

  if (status === "loading") return <Loading />;
  if (status === "error") return <ErrorState onRetry={load} />;

  const liveMode = data?.live_mode ?? false;

  if (!data?.has_data) {
    return (
      <div>
        <div className="mb-6 flex items-center justify-between">{RangeSelector}<SimuladoBadge liveMode={liveMode} /></div>
        <EmptyState icon={Music2} title="Aún no hay métricas" desc="Cuando tus lanzamientos empiecen a acumular reproducciones, verás tus insights aquí." testId="dashboard-empty" />
      </div>
    );
  }

  const platMax = Math.max(...(data.platforms || []).map((p) => p.streams), 1);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Flow-lines header banner (subtle echo of the public hero) */}
      <div className="relative overflow-hidden rounded-2xl bg-[#0A0A0A] px-6 py-6 lg:px-8" data-testid="dashboard-header-banner">
        <FlowLines className="absolute inset-0 h-full w-full opacity-60" lineColor="rgba(255,255,255,0.16)" nodeColor="rgba(255,255,255,0.92)" lines={9} />
        <div className="relative z-10">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">Panel de control</div>
          <h2 className="mt-1 font-serif text-2xl font-bold tracking-tight text-white lg:text-3xl">Métricas &amp; Insights</h2>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {RangeSelector}
        <SimuladoBadge liveMode={liveMode} />
      </div>

      {/* Totals */}
      <div id="resumen" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-streams" label="Reproducciones" value={fmtNum(data.totals.streams)} icon={Play} variant="dark" />
        <StatCard testId="stat-listeners" label="Oyentes" value={fmtNum(data.totals.listeners)} icon={Users} variant="dark" />
        <StatCard testId="stat-downloads" label="Descargas" value={fmtNum(data.totals.downloads)} icon={DownloadIcon} />
        <StatCard testId="stat-playlisting" label="Playlists" value={fmtNum(data.totals.playlisting)} icon={ListMusic} />
      </div>

      {/* Time series */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="streams-chart">
        <SectionTitle>Evolución de reproducciones</SectionTitle>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={60}>
            <AreaChart data={data.series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#CA0808" stopOpacity={0.28} />
                  <stop offset="55%" stopColor="#CA0808" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#CA0808" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={chartTickDate} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} labelFormatter={(l) => chartTickDate(l)} formatter={(v) => [fmtNum(v), "Streams"]} />
              <Area type="monotone" dataKey="streams" stroke="#CA0808" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Platforms + Ages */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div id="plataformas" className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="platforms-breakdown">
          <SectionTitle>Por plataforma</SectionTitle>
          <div className="space-y-4">
            {(data.platforms || []).map((p) => (
              <PlatformBar key={p.name} p={p} max={platMax} onClick={() => openPlatform(p)} />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="ages-breakdown">
          <SectionTitle>Demografía (edad)</SectionTitle>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={(data.ages || []).map((a) => ({ name: a.name, value: a.pct }))} dataKey="value" nameKey="name" innerRadius={42} outerRadius={66} paddingAngle={2} stroke="none">
                  {(data.ages || []).map((_, i) => <Cell key={i} fill={AGE_COLORS[i % AGE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtPct(v)} contentStyle={{ borderRadius: 10, border: "1px solid #17171733", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
            {(data.ages || []).map((a, i) => (
              <div key={a.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-[#171717]"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: AGE_COLORS[i % AGE_COLORS.length] }} />{a.name}</span>
                <span className="font-mono font-semibold text-[#171717]">{fmtPct(a.pct)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Countries + Cities (vinotinto) */}
      <div id="audiencia" className="grid gap-6 lg:grid-cols-2">
        <GeoCard title="Top países" icon={Globe2} rows={data.countries || []} testId="countries-rank" withFlags onRow={openCountry} />
        <GeoCard title="Top ciudades" icon={MapPin} rows={data.cities || []} testId="cities-rank" onRow={openCity} />
      </div>

      {/* Top tracks */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="top-tracks">
        <SectionTitle>Canciones destacadas</SectionTitle>
        <div className="space-y-2">
          {(data.top_tracks || []).map((t, i) => (
            <div key={t.title} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-slate-400">#{i + 1}</span>
                <span className="font-medium text-slate-800">{t.title}</span>
              </div>
              <span className="font-mono text-sm tabular-nums text-slate-600">{fmtNum(t.streams)} streams</span>
            </div>
          ))}
        </div>
      </div>

      <BreakdownModal open={!!bd} data={bd} onClose={() => setBd(null)} />
    </div>
  );
}
